import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { hapticTap } from '@/utils/haptics';

// =============================================================================
// STANDARDIZED BUTTON COMPONENT
// =============================================================================
// Purpose: Ensure consistency, accessibility, and proper touch targets across
// the application. All buttons meet 44x44px minimum touch target size.

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'warning' | 'outline';
/**
 * `inline` is a text trigger inside dense content -- a filename in a table row,
 * a "show more" inside a paragraph -- where the row or the surrounding text is
 * the touch target and a 44px box around the words would break the layout it
 * sits in.
 *
 * It exists because 82 call sites were already doing it by hand, with
 * `!min-h-0 !min-w-0 !p-0` overriding the size system from the outside. That
 * hid every one of them from the touch-target sweep in this file's own header
 * promise, and each was hand-tuned, so they drifted. A named size makes the
 * exception greppable and reviewable instead of buried in a className.
 */
type ButtonSize = 'inline' | 'xs' | 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  children?: React.ReactNode;
  /**
   * Render as a router link instead of a button, keeping identical styling.
   *
   * Use this whenever the control's job is to GO somewhere rather than to DO
   * something. It is not cosmetic: a real anchor restores middle-click and
   * Cmd/Ctrl-click to open in a new tab, "Copy link address", and the browser's
   * status-bar preview of the destination — all of which an operator working
   * several records at once relies on, and all of which `onClick={() =>
   * navigate(...)}` silently throws away. Assistive tech also announces it as a
   * link, which is what it is.
   *
   * A disabled or loading control still renders as a <button>, because there is
   * no such thing as a disabled link.
   */
  to?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      icon,
      iconPosition = 'left',
      loading = false,
      disabled,
      className = '',
      children,
      type = 'button',
      to,
      ...props
    },
    ref
  ) => {
    /*
     * Variant styles.
     *
     * DISABLED LABELS TAKE DARK TEXT, NOT WHITE. Every filled variant mutes to a
     * light `-300` tint when disabled, and `text-white` on a 75%-lightness tint
     * is unreadable: measured in the browser, primary was 1.67:1 and success
     * 1.40:1 -- the label is a ghost. That is the state the app's front door
     * ships in, since Login's "Sign In" is disabled until the form is filled,
     * so the first control every user meets could not be read.
     *
     * `warning` already had the answer (dark `amber-700` on `amber-200`, 16.9:1)
     * because no usable yellow carries white text. The other three now follow
     * it: `-900` on `-300` clears 7:1. The muted FILL plus `opacity-60` and
     * `cursor-not-allowed` still say "unavailable"; only the label changed.
     *
     * Safe for tenant branding: `theme-utils.ts` generates `-300` at 75%
     * lightness and `-900` at 24% from the brand hue, so the gap holds whatever
     * colour a county picks.
     *
     * The UNFILLED variants (secondary, ghost, outline) needed the same
     * treatment. They disabled to `bg-gray-100` + `text-gray-400` with no dark
     * counterpart, so in dark mode a disabled control took a near-WHITE fill and
     * a mid-grey label -- then the base `disabled:opacity-60` washed both out
     * again. On the shelter Residents tab, "Export CSV" and "Screen All" were
     * effectively unreadable. Each now carries an explicit dark-mode pair, and
     * the label is dark enough (light) / light enough (dark) to survive the
     * opacity multiply.
     */
    const variantStyles: Record<ButtonVariant, string> = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-300 disabled:text-primary-900',
      secondary:
        'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:text-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-300',
      danger:
        'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:text-red-900',
      // green-600 under white text measures 3.3:1, short of the 4.5:1 that
      // normal-size text needs. One step darker clears it at 5.0:1.
      success:
        'bg-green-700 text-white hover:bg-green-800 disabled:bg-green-300 disabled:text-green-900',
      // White on yellow-500 is 1.92:1 -- the worst contrast in the app. No
      // usable yellow carries white text: amber-600, dark enough to look
      // muddy, still only reaches 3.1:1. Yellow takes dark text instead.
      warning:
        'bg-amber-400 text-amber-950 hover:bg-amber-500 disabled:bg-amber-200 disabled:text-amber-700',
      ghost:
        'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 disabled:text-gray-600 dark:disabled:text-gray-300',
      outline:
        'border border-theme-secondary bg-surface-primary text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:text-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-300',
    };

    // Size styles - sm/md/lg meet 44px minimum touch target.
    // xs is for desktop-dense UIs only (tool palettes, menu rows) — do not use
    // on mobile-reachable surfaces.
    // min-w matters as much as min-h and was missing: this file's own header
    // promises "44x44px", but only the height was enforced, so a button with a
    // very short label ("All", "1", a lone icon) measured 40-42px wide in
    // production while standing 44px tall. IconButton has always set both.
    const sizeStyles: Record<ButtonSize, string> = {
      inline: 'p-0 gap-1 text-inherit',
      xs: 'min-h-[32px] min-w-[32px] px-2 text-xs gap-1.5 font-medium',
      sm: 'min-h-[44px] min-w-[44px] px-3 text-sm gap-1.5',
      md: 'min-h-[44px] min-w-[44px] px-4 text-base gap-2',
      lg: 'min-h-[48px] min-w-[48px] px-6 text-lg gap-2.5', // 48px for extra prominence
    };

    // Icon sizing based on button size
    const iconSizeClass =
      size === 'inline' ? 'h-4 w-4'
      : size === 'xs' ? 'h-4 w-4'
      : size === 'sm' ? 'h-4 w-4'
      : size === 'lg' ? 'h-6 w-6'
      : 'h-5 w-5';

    // Focus ring: one consistent 2px primary ring (the --interactive-focus-ring
    // token) with a surface-matched offset, for every variant in both themes.
    const baseStyles =
      'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface-primary)] disabled:cursor-not-allowed disabled:opacity-60';

    const widthStyle = fullWidth ? 'w-full' : '';

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && !loading) {
        hapticTap();
      }
      props.onClick?.(e);
    };

    const isDisabled = disabled || loading;

    const mergedClassName = twMerge(
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      widthStyle,
      className
    );

    const content = (
      <>
        {loading && (
          <svg
            className={`animate-spin ${iconSizeClass}`}
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && icon && iconPosition === 'left' && (
          <span className={iconSizeClass} aria-hidden="true">
            {icon}
          </span>
        )}
        {children && <span style={{ display: 'contents' }}>{children}</span>}
        {!loading && icon && iconPosition === 'right' && (
          <span className={iconSizeClass} aria-hidden="true">
            {icon}
          </span>
        )}
        {loading && <span className="sr-only">Loading...</span>}
      </>
    );

    // A disabled link is not a thing — fall back to a real (disabled) button so
    // the control stays genuinely inert rather than merely looking inert.
    if (to && !isDisabled) {
      return (
        <Link
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          to={to}
          className={mergedClassName}
          {...(props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          onClick={handleClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        className={mergedClassName}
        {...props}
        onClick={handleClick}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

// =============================================================================
// ICON BUTTON COMPONENT
// =============================================================================
// Purpose: Icon-only buttons with proper ARIA labels and touch targets

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string; // Required for accessibility
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, variant = 'ghost', size = 'md', className = '', ...props }, ref) => {
    // Size styles - sm/md/lg meet 44px minimum touch target (xs is desktop-only)
    const sizeStyles: Record<ButtonSize, string> = {
      inline: 'p-0',
      xs: 'min-h-[32px] min-w-[32px] p-1.5',
      sm: 'min-h-[44px] min-w-[44px] p-2',
      md: 'min-h-[44px] min-w-[44px] p-2.5',
      lg: 'min-h-[48px] min-w-[48px] p-3',
    };

    const iconSizeClass =
      size === 'inline' ? 'h-4 w-4'
      : size === 'xs' ? 'h-4 w-4'
      : size === 'sm' ? 'h-5 w-5'
      : size === 'lg' ? 'h-6 w-6'
      : 'h-5 w-5';

    const variantStyles: Record<ButtonVariant, string> = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700',
      secondary:
        'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600',
      danger:
        'bg-red-600 text-white hover:bg-red-700',
      success:
        'bg-green-700 text-white hover:bg-green-800',
      warning:
        'bg-amber-400 text-amber-950 hover:bg-amber-500',
      ghost:
        'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
      outline:
        'border border-theme-secondary bg-surface-primary text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600',
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!props.disabled) {
        hapticTap();
      }
      props.onClick?.(e);
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        aria-disabled={props.disabled || undefined}
        className={twMerge(
          'inline-flex items-center justify-center rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface-primary)] touch-manipulation disabled:cursor-not-allowed disabled:opacity-60',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
        onClick={handleClick}
      >
        <span className={iconSizeClass} aria-hidden="true">
          {icon}
        </span>
        <span className="sr-only">{label}</span>
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
